import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CheckCircle2, Loader2, X as IconX, Star, Heart, Send, Sparkles, MessageSquare, Share2,
} from "lucide-react";
import { toast } from "sonner";

export default function PortalFeedback() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [satisfaccion, setSatisfaccion] = useState(0);
  const [nps, setNps] = useState(null);
  const [cumplimosPlazo, setCumplimosPlazo] = useState(null);
  const [profesionalidad, setProfesionalidad] = useState(0);
  const [calidad, setCalidad] = useState(0);
  const [repetirias, setRepetirias] = useState("");
  const [comentarios, setComentarios] = useState("");
  const [autorizaPub, setAutorizaPub] = useState(false);
  const [autorizaEtiq, setAutorizaEtiq] = useState(false);
  const [socialInsta, setSocialInsta] = useState("");
  const [socialFb, setSocialFb] = useState("");
  const [socialTk, setSocialTk] = useState("");
  const [nombre, setNombre] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/portal-feedback/${token}`);
      setData(data);
      api.post(`/portal-feedback/${token}/visto`).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submit = async () => {
    if (!satisfaccion || nps === null) {
      toast.error("Por favor, danos al menos las 2 valoraciones principales");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/portal-feedback/${token}/responder`, {
        satisfaccion,
        nps,
        cumplimos_plazo: cumplimosPlazo,
        profesionalidad: profesionalidad || null,
        calidad: calidad || null,
        repetirias: repetirias || null,
        comentarios: comentarios.trim() || null,
        autoriza_publicacion: autorizaPub,
        autoriza_etiquetar: autorizaEtiq,
        social_instagram: autorizaPub ? socialInsta : null,
        social_facebook: autorizaPub ? socialFb : null,
        social_tiktok: autorizaPub ? socialTk : null,
        nombre_respuesta: nombre.trim() || null,
      });
      toast.success("¡Gracias por tu feedback!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando feedback");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-6 max-w-md text-center" data-testid="feedback-error">
          <IconX className="w-10 h-10 text-red-400 mx-auto mb-2" />
          <div className="text-red-200 font-bold mb-1">Enlace no disponible</div>
          <div className="text-zinc-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const cliente = data.cliente || {};
  const peticion = data.peticion || {};

  // Ya respondido
  if (data.ya_respondido) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8" data-testid="feedback-already-done">
        <div className="max-w-xl mx-auto">
          <Header cliente={cliente} peticion={peticion} />
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-8 text-center mt-6">
            <Heart className="w-16 h-16 mx-auto text-pink-400 mb-3 fill-pink-400" />
            <div className="text-emerald-300 font-bold text-xl mb-2">¡Gracias por tu feedback!</div>
            <div className="text-zinc-400 text-sm">Ya hemos recibido tus respuestas. Tu opinión nos ayuda a mejorar día a día.</div>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8" data-testid="feedback-form">
      <div className="max-w-xl mx-auto">
        <Header cliente={cliente} peticion={peticion} />

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 mt-6 space-y-6">
          <div>
            <div className="text-amber-300 font-bold flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4" />
              ¡Esperamos que el montaje haya quedado perfecto!
            </div>
            <p className="text-sm text-zinc-400">Tu opinión nos ayuda enormemente a seguir creciendo. Sólo te llevará 1 minuto.</p>
          </div>

          {/* Satisfacción global */}
          <Section title="¿Cómo valoras nuestro trabajo en general?" testid="section-satisfaccion">
            <StarPicker value={satisfaccion} onChange={setSatisfaccion} max={5} testid="stars-satisfaccion" />
          </Section>

          {/* NPS */}
          <Section title="¿Nos recomendarías a otro cliente o conocido?" subtitle="(0 = nada probable, 10 = totalmente)" testid="section-nps">
            <NPSPicker value={nps} onChange={setNps} />
          </Section>

          {/* Plazo */}
          <Section title="¿Cumplimos el plazo prometido?" testid="section-plazo">
            <div className="flex gap-2">
              <Button
                variant={cumplimosPlazo === true ? "default" : "outline"}
                onClick={() => setCumplimosPlazo(true)}
                className={cumplimosPlazo === true ? "bg-emerald-500 hover:bg-emerald-600 text-zinc-900" : ""}
                data-testid="btn-plazo-si"
              >
                ✅ Sí
              </Button>
              <Button
                variant={cumplimosPlazo === false ? "default" : "outline"}
                onClick={() => setCumplimosPlazo(false)}
                className={cumplimosPlazo === false ? "bg-rose-500 hover:bg-rose-600 text-white" : ""}
                data-testid="btn-plazo-no"
              >
                ❌ No
              </Button>
            </div>
          </Section>

          {/* Profesionalidad */}
          <Section title="Profesionalidad del equipo de montaje" testid="section-prof">
            <StarPicker value={profesionalidad} onChange={setProfesionalidad} max={5} testid="stars-prof" />
          </Section>

          {/* Calidad */}
          <Section title="Calidad del material y acabado" testid="section-calidad">
            <StarPicker value={calidad} onChange={setCalidad} max={5} testid="stars-calidad" />
          </Section>

          {/* Repetirías */}
          <Section title="¿Repetirías con nosotros?" testid="section-repetirias">
            <div className="flex flex-wrap gap-2">
              {[
                { v: "si", label: "🟢 Sí, sin duda" },
                { v: "probable", label: "🟡 Probablemente" },
                { v: "no", label: "🔴 No lo creo" },
              ].map((opt) => (
                <Button
                  key={opt.v}
                  variant={repetirias === opt.v ? "default" : "outline"}
                  onClick={() => setRepetirias(opt.v)}
                  className={repetirias === opt.v ? "bg-amber-500 hover:bg-amber-600 text-zinc-900" : ""}
                  data-testid={`btn-repetirias-${opt.v}`}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </Section>

          {/* Comentarios */}
          <Section title="¿Algún comentario, sugerencia o mejora?" optional testid="section-comentarios">
            <Textarea
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
              placeholder="Lo que mejorarías, lo que más te gustó, sugerencias..."
              rows={3}
              className="bg-zinc-950 border-zinc-700"
              data-testid="input-comentarios"
            />
          </Section>

          {/* Autorización publicación */}
          <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-5 space-y-4" data-testid="section-publicacion">
            <div className="flex items-start gap-3">
              <Share2 className="w-5 h-5 text-pink-400 mt-0.5" />
              <div>
                <div className="text-pink-200 font-bold text-sm">¿Nos autorizas a publicar este trabajo en redes sociales?</div>
                <div className="text-zinc-400 text-xs mt-1">Compartiríamos fotos del resultado en nuestras redes (Instagram, Facebook, TikTok).</div>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={autorizaPub}
                onCheckedChange={setAutorizaPub}
                data-testid="checkbox-autoriza-publicacion"
                className="mt-0.5"
              />
              <span className="text-sm text-zinc-200">Autorizo a DNF a publicar las fotos del montaje en sus redes sociales</span>
            </label>
            {autorizaPub && (
              <>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={autorizaEtiq}
                    onCheckedChange={setAutorizaEtiq}
                    data-testid="checkbox-autoriza-etiquetar"
                    className="mt-0.5"
                  />
                  <span className="text-sm text-zinc-200">Autorizo a etiquetar mi marca/negocio</span>
                </label>
                {autorizaEtiq && (
                  <div className="space-y-2 pl-6 border-l-2 border-pink-500/30">
                    <div className="text-xs text-zinc-400">Comparte tus usuarios para que te etiquetemos:</div>
                    <SocialInput label="Instagram" value={socialInsta} onChange={setSocialInsta} placeholder="tu_usuario_instagram" testid="input-insta" />
                    <SocialInput label="Facebook" value={socialFb} onChange={setSocialFb} placeholder="tu.pagina.facebook" testid="input-fb" />
                    <SocialInput label="TikTok" value={socialTk} onChange={setSocialTk} placeholder="tu_usuario_tiktok" testid="input-tk" />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Nombre */}
          <div>
            <Label className="text-xs text-zinc-400">Tu nombre <span className="text-zinc-600">(opcional)</span></Label>
            <Input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Para que sepamos quién responde"
              className="bg-zinc-950 border-zinc-700 mt-1"
              data-testid="input-nombre"
            />
          </div>

          {/* Submit */}
          <Button
            onClick={submit}
            disabled={saving || !satisfaccion || nps === null}
            className="w-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-600 hover:to-pink-600 text-zinc-900 font-bold py-6 text-base"
            data-testid="btn-enviar-feedback"
          >
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
            Enviar mi feedback
          </Button>
        </div>

        <Footer />
      </div>
    </div>
  );
}

function Header({ cliente, peticion }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      {cliente.logo_url && (
        <img src={cliente.logo_url} alt={cliente.nombre} className="w-12 h-12 rounded-full object-contain bg-white p-1" />
      )}
      <div className="flex-1">
        <div className="text-zinc-300 text-sm">{cliente.nombre || "Cliente"}</div>
        <div className="text-zinc-500 text-xs font-mono">{peticion.codigo || "—"}</div>
      </div>
      <img src="/logo-dnf.png" alt="DNF" className="h-10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </div>
  );
}

function Footer() {
  return (
    <div className="text-center mt-8 text-xs text-zinc-600 space-y-1">
      <div>RotulaciónDNF · Producción Gráfica</div>
      <div>☎ +34 624 49 12 71 · <a href="https://rotulaciondnf.com" className="underline">rotulaciondnf.com</a></div>
    </div>
  );
}

function Section({ title, subtitle, children, optional, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">
        <MessageSquare className="w-3.5 h-3.5 text-amber-400" />
        {title}
        {optional && <span className="text-xs text-zinc-500 font-normal">(opcional)</span>}
      </div>
      {subtitle && <div className="text-xs text-zinc-500 mb-2 -mt-1">{subtitle}</div>}
      {children}
    </div>
  );
}

function StarPicker({ value, onChange, max = 5, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {Array.from({ length: max }).map((_, i) => {
        const n = i + 1;
        const filled = n <= value;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="transition-transform hover:scale-110"
            data-testid={`${testid}-${n}`}
          >
            <Star
              className={`w-9 h-9 ${filled ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`}
            />
          </button>
        );
      })}
    </div>
  );
}

function NPSPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-11 gap-1" data-testid="nps-picker">
      {Array.from({ length: 11 }).map((_, n) => {
        const selected = value === n;
        const color =
          n >= 9 ? "bg-emerald-500 text-zinc-900"
          : n >= 7 ? "bg-amber-500 text-zinc-900"
          : "bg-rose-500/80 text-white";
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`h-9 rounded text-xs font-bold transition-all ${
              selected ? `${color} scale-110 shadow-lg` : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"
            }`}
            data-testid={`nps-${n}`}
          >
            {n}
          </button>
        );
      })}
    </div>
  );
}

function SocialInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-zinc-500 w-20">{label}</span>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-zinc-950 border-zinc-700 text-sm h-8"
        data-testid={testid}
      />
    </div>
  );
}
